import React, { useEffect, useState } from 'react';
import { useField } from 'formik';
import { Flex } from '../../styles/layout';
import { CSSProperties, useTheme } from 'styled-components/macro';
import { IoMdCloseCircle } from 'react-icons/io';
import { FormikLabel } from '../FormikLabel';
import { FormikImageUploadErrorMessage } from '../FormikImageUploadError';
import { ImageUploadAndCropModal } from '../../modal/image-upload/ImageUploadAndCropModal';
import {
  ImageContainer,
  ProgressBar,
  ProgressContainer,
  Image,
  PlaceholderBox,
} from './styles';

interface IFormikImageUploadProps {
  name: string;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  text?: string;
  info?: string | React.ReactNode;
  onSave?: (croppedImageFile: File) => void;
  extendStyles?: {
    labelStyles?: CSSProperties;
    buttonWrapper?: CSSProperties;
    asteriskStyles?: CSSProperties;
    imageStyles?: CSSProperties;
    containerStyles?: CSSProperties;
    placeholderStyles?: CSSProperties;
  };
  progress?: number;
  hideError?: boolean;
  aspectRatio?: number;
  targetWidth?: number;
  useCropper?: boolean;
}

export const FormikImageUpload = ({
  isRequired = false,
  disabled = false,
  onSave,
  extendStyles,
  info,
  label,
  text = 'Click to upload image',
  progress,
  hideError = false,
  aspectRatio,
  targetWidth,
  useCropper = true,
  ...props
}: IFormikImageUploadProps) => {
  const theme = useTheme();
  const [field, meta, helpers] = useField(props);
  const url = field.value?.url || '';

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => {
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    helpers.setTouched(false);
  }, []);

  const handleOnSave = async (croppedImageFile: File) => {
    if (!!onSave) {
      onSave(croppedImageFile);
      return;
    }

    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    const tempUrl = URL.createObjectURL(croppedImageFile);
    setImageUrl(tempUrl);
    helpers.setValue({ file: croppedImageFile, url: tempUrl });
    toggleModal();
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!url) return;
    helpers.setTouched(true);
    helpers.setValue({ url: '', file: null });
  };

  return (
    <Flex width='100%' gap='0px'>
      <Flex gap='8px'>
        {label && (
          <FormikLabel
            label={label}
            isRequired={isRequired}
            disabled={!!disabled}
            info={info}
            labelStyles={extendStyles?.labelStyles}
            asteriskStyles={extendStyles?.asteriskStyles}
          />
        )}
        <Flex width='100%' gap='0px'>
          {progress && progress > 0 ? (
            <ProgressContainer>
              <ProgressBar progress={progress} />
              <span style={{ zIndex: 1, position: 'relative' }}>
                {progress < 100 ? `Uploading... ${progress}%` : 'Processing...'}
              </span>
            </ProgressContainer>
          ) : url ? (
            <ImageContainer
              style={{ ...extendStyles?.containerStyles }}
              onClick={toggleModal}
              disabled={disabled}
            >
              <Image
                src={url}
                alt='image-uploader'
                style={{ ...extendStyles?.imageStyles }}
              />
              <IoMdCloseCircle
                onClick={handleRemoveImage}
                size={24}
                color={theme.colors.neutral[60]}
                style={{
                  position: 'absolute',
                  top: '-14px',
                  right: '-14px',
                  border: `3px solid ${theme.colors.white[100]}`,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.white[100],
                  cursor: 'pointer',
                }}
              />
            </ImageContainer>
          ) : (
            <PlaceholderBox
              onClick={toggleModal}
              style={{
                ...extendStyles?.containerStyles,
                ...extendStyles?.placeholderStyles,
              }}
            >
              {text}
            </PlaceholderBox>
          )}
        </Flex>
        <ImageUploadAndCropModal
          isOpen={isOpen}
          onClose={toggleModal}
          onSave={handleOnSave}
          isSaving={false}
          headerText='Upload Image'
          saveButtonText='Upload'
          aspectRatio={aspectRatio}
          targetWidth={targetWidth}
          useCropper={useCropper}
        />
      </Flex>
      {!hideError && (
        <FormikImageUploadErrorMessage name={props.name} meta={meta} />
      )}
    </Flex>
  );
};
